import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, waLink } from "../lib/api";
import { useLang } from "../lib/i18n";
import { pick } from "../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LogoMark } from "../components/Logo";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const digits = (p) => (p || "").replace(/\D/g, "");

const StatusCell = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger size="sm" className="w-[130px] h-9 text-xs" data-testid={testid}>{value}</SelectTrigger>
    <SelectContent>
      {options.map((o) => <SelectItem key={o} value={o} data-testid={`${testid}-${o}`}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

const BookingsTab = ({ t }) => {
  const qc = useQueryClient();
  const { data: bookings = [], isLoading } = useQuery({ queryKey: ["bookings"], queryFn: () => api.get("/bookings").then(r => r.data) });
  const setStatus = async (id, status) => {
    try { await api.patch(`/bookings/${id}`, { status }); qc.invalidateQueries({ queryKey: ["bookings"] }); toast.success(status); }
    catch { toast.error(t.common.error); }
  };
  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-forest-600" /></div>;
  if (!bookings.length) return <p className="py-16 text-center text-muted-foreground" data-testid="bookings-empty">{t.admin.none}</p>;
  return (
    <div className="rounded-2xl border border-forest-100 bg-white overflow-x-auto" data-testid="bookings-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-mist hover:bg-mist">
            <TableHead>{t.admin.col.customer}</TableHead>
            <TableHead>{t.admin.col.service}</TableHead>
            <TableHead>{t.admin.col.when}</TableHead>
            <TableHead>{t.admin.col.address}</TableHead>
            <TableHead>{t.admin.col.status}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map((b) => (
            <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
              <TableCell>
                <div className="font-semibold text-forest-900">{b.name}</div>
                <div className="text-xs text-muted-foreground">{b.phone}{b.email ? ` · ${b.email}` : ""}</div>
              </TableCell>
              <TableCell>
                <div className="font-medium">{b.service_name}</div>
                <div className="text-xs uppercase tracking-wide font-bold text-forest-600">{b.package === "full" ? t.admin.col.full.replace(" (Rs)", "") : t.admin.col.half.replace(" (Rs)", "")}</div>
              </TableCell>
              <TableCell>
                <div className="font-medium">{b.date}</div>
                <div className="text-xs text-muted-foreground">{b.time}</div>
              </TableCell>
              <TableCell className="max-w-[220px]">
                <div className="text-sm truncate">{b.address}</div>
                {b.notes ? <div className="text-xs text-muted-foreground truncate">“{b.notes}”</div> : null}
              </TableCell>
              <TableCell>
                <StatusCell value={b.status} onChange={(v) => setStatus(b.id, v)} options={["new", "confirmed", "done", "cancelled"]} testid={`booking-status-${b.id}`} />
              </TableCell>
              <TableCell>
                <a href={waLink(`Hello ${b.name}!`)} target="_blank" rel="noopener noreferrer" data-testid={`booking-wa-${b.id}`} className="inline-flex text-forest-700 hover:text-forest-900" title="WhatsApp">
                  <ExternalLink size={16} />
                </a>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const QuotesTab = ({ t }) => {
  const qc = useQueryClient();
  const { data: quotes = [], isLoading } = useQuery({ queryKey: ["quotes"], queryFn: () => api.get("/quotes").then(r => r.data) });
  const setStatus = async (id, status) => {
    try { await api.patch(`/quotes/${id}`, { status }); qc.invalidateQueries({ queryKey: ["quotes"] }); toast.success(status); }
    catch { toast.error(t.common.error); }
  };
  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-forest-600" /></div>;
  if (!quotes.length) return <p className="py-16 text-center text-muted-foreground" data-testid="quotes-empty">{t.admin.none}</p>;
  return (
    <div className="rounded-2xl border border-forest-100 bg-white overflow-x-auto" data-testid="quotes-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-mist hover:bg-mist">
            <TableHead>{t.admin.col.customer}</TableHead>
            <TableHead>{t.admin.col.service}</TableHead>
            <TableHead>{t.quote?.message || "Message"}</TableHead>
            <TableHead>{t.admin.col.status}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {quotes.map((q) => (
            <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
              <TableCell>
                <div className="font-semibold text-forest-900">{q.name}</div>
                <div className="text-xs text-muted-foreground">{q.phone}{q.email ? ` · ${q.email}` : ""}</div>
                <div className="text-[11px] text-muted-foreground/70 mt-0.5">{fmtDate(q.created_at)}</div>
              </TableCell>
              <TableCell className="font-medium">{q.service}</TableCell>
              <TableCell className="max-w-[320px] text-sm">{q.message}</TableCell>
              <TableCell>
                <StatusCell value={q.status} onChange={(v) => setStatus(q.id, v)} options={["new", "sent", "won", "closed"]} testid={`quote-status-${q.id}`} />
              </TableCell>
              <TableCell>
                <a href={waLink(`Hello ${q.name}!`)} target="_blank" rel="noopener noreferrer" data-testid={`quote-wa-${q.id}`} className="inline-flex text-forest-700 hover:text-forest-900" title="WhatsApp">
                  <ExternalLink size={16} />
                </a>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const PricesTab = ({ t, lang }) => {
  const qc = useQueryClient();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then(r => r.data) });
  const [draft, setDraft] = useState({});
  useEffect(() => {
    const d = {};
    services.forEach((s) => { d[s.slug] = { full: String(s.full_price), half: String(s.half_price) }; });
    setDraft(d);
  }, [services]);
  const save = async (slug) => {
    try {
      await api.patch(`/services/${slug}`, { full_price: Number(draft[slug].full), half_price: Number(draft[slug].half) });
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success(t.admin.col.saved);
    } catch { toast.error(t.common.error); }
  };
  return (
    <div className="rounded-2xl border border-forest-100 bg-white overflow-x-auto" data-testid="prices-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-mist hover:bg-mist">
            <TableHead>{t.admin.col.service}</TableHead>
            <TableHead>{t.admin.col.full}</TableHead>
            <TableHead>{t.admin.col.half}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {services.map((s) => (
            <TableRow key={s.slug} data-testid={`price-row-${s.slug}`}>
              <TableCell className="font-semibold text-forest-900">{pick(s, "name", lang)}</TableCell>
              <TableCell>
                <Input type="number" min="0" className="w-28" value={draft[s.slug]?.full ?? ""} onChange={(e) => setDraft({ ...draft, [s.slug]: { ...draft[s.slug], full: e.target.value } })} data-testid={`price-full-${s.slug}`} />
              </TableCell>
              <TableCell>
                <Input type="number" min="0" className="w-28" value={draft[s.slug]?.half ?? ""} onChange={(e) => setDraft({ ...draft, [s.slug]: { ...draft[s.slug], half: e.target.value } })} data-testid={`price-half-${s.slug}`} />
              </TableCell>
              <TableCell>
                <Button onClick={() => save(s.slug)} data-testid={`price-save-${s.slug}`} className="rounded-full bg-forest-700 hover:bg-forest-800 h-9 px-4 text-white text-sm">
                  <Save size={14} /> {t.admin.col.save}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default function Admin() {
  const { t, lang } = useLang();
  return (
    <div className="min-h-screen bg-mist/70" data-testid="admin-page">
      <header className="bg-white border-b border-forest-100">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={34} />
            <div>
              <h1 className="font-display font-bold text-forest-950 text-lg leading-none">{t.admin.title}</h1>
              <p className="text-xs text-muted-foreground mt-1">{t.admin.sub}</p>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10">
        <Tabs defaultValue="bookings" data-testid="admin-tabs">
          <TabsList className="bg-white border border-forest-100">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">{t.admin.tabs.bookings}</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes">{t.admin.tabs.quotes}</TabsTrigger>
            <TabsTrigger value="prices" data-testid="admin-tab-prices">{t.admin.tabs.prices}</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings" className="mt-6"><BookingsTab t={t} /></TabsContent>
          <TabsContent value="quotes" className="mt-6"><QuotesTab t={t} /></TabsContent>
          <TabsContent value="prices" className="mt-6"><PricesTab t={t} lang={lang} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}